import { useState, type FormEvent } from "react";
import { PASSWORD_MIN, Username } from "@empire/shared";
import { useSessionStore } from "../../store/session.js";
import { markTutorialSeen } from "../../store/tutorial.js";
import { useUiStore } from "../../store/ui.js";

/**
 * Save this village under a username, or log in to a saved one (D-116).
 *
 * Friends test #2 lost every village played inside WhatsApp's browser: the
 * village was safe on the server, but the only key to it lived in a browser
 * that forgot it. A username and password is a key the player carries.
 */
export function AccountSheet(): JSX.Element | null {
  const mode = useUiStore((s) => s.account);
  const open = useUiStore((s) => s.openAccount);
  const username = useSessionStore((s) => s.username);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  if (!mode) return null;

  const close = (): void => {
    open(null);
    setError(null);
    setPassword("");
    setSaved(null);
  };
  const switchTo = (next: "save" | "login"): void => {
    setError(null);
    open(next);
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setError(null);
    if (mode === "save") {
      const checked = Username.safeParse(name);
      if (!checked.success) {
        setError(`Username: ${checked.error.issues[0]?.message ?? "not allowed"}.`);
        return;
      }
      if (password.length < PASSWORD_MIN) {
        setError(`Password: at least ${PASSWORD_MIN} characters.`);
        return;
      }
    }
    setBusy(true);
    const problem =
      mode === "save"
        ? await useSessionStore.getState().register(name, password)
        : await useSessionStore.getState().logIn(name, password);
    setBusy(false);
    if (problem) {
      setError(problem);
      return;
    }
    if (mode === "save") {
      setSaved(name.trim());
      setPassword("");
      return;
    }
    // Logged in: a different village now belongs to this device. Start the app
    // afresh from the new session rather than patch every store by hand.
    markTutorialSeen();
    globalThis.location?.reload();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end bg-black/60" data-testid="account-sheet">
      <div
        className="w-full rounded-t-2xl border-t border-panel-edge bg-bg-raised px-5 pt-5"
        style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display text-xl text-gold">
            {saved ? "Village saved" : mode === "save" ? "Save your village" : "Log in"}
          </h2>
          <button
            type="button"
            onClick={close}
            data-testid="account-close"
            className="h-9 rounded-lg px-3 text-xs font-semibold text-ink-dim"
          >
            Close
          </button>
        </div>

        {saved ? (
          <div data-testid="account-saved">
            <p className="mt-2 text-sm text-ink">
              Your village is saved as <b>{saved}</b>. On any phone or browser, tap Log in and use this
              username and password.
            </p>
            <button
              type="button"
              onClick={close}
              className="mt-4 h-12 w-full rounded-lg bg-gold text-sm font-bold text-bg"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={(e) => void submit(e)}>
            <p className="mt-1 text-[12px] text-ink-dim">
              {mode === "save"
                ? "Pick a username and password so you can come back to this village on any phone or browser. Your username is your name on the map."
                : username === null
                  ? "Open a village you saved before. The guest village on this device will be left behind."
                  : "Open a village you saved before."}
            </p>
            <label className="mt-4 block text-[11px] font-semibold text-ink-dim">
              Username
              <input
                data-testid="account-username"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={20}
                className="mt-1 h-12 w-full rounded-lg border border-panel-edge bg-panel px-3 text-base text-ink outline-none focus:border-gold"
              />
            </label>
            <label className="mt-3 block text-[11px] font-semibold text-ink-dim">
              Password
              <div className="mt-1 flex gap-2">
                <input
                  data-testid="account-password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === "save" ? "new-password" : "current-password"}
                  className="h-12 min-w-0 flex-1 rounded-lg border border-panel-edge bg-panel px-3 text-base text-ink outline-none focus:border-gold"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="h-12 shrink-0 rounded-lg border border-panel-edge bg-panel px-3 text-xs font-semibold text-ink-dim"
                >
                  {show ? "Hide" : "Show"}
                </button>
              </div>
            </label>
            {mode === "save" ? (
              <p className="mt-1 text-[11px] text-ink-dim">
                3–20 letters, digits or _ · password at least {PASSWORD_MIN} characters. There is
                no password reset yet, so pick one you will remember.
              </p>
            ) : null}
            {error ? (
              <p className="mt-3 text-sm text-warn" data-testid="account-error">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={busy}
              data-testid="account-submit"
              className="mt-4 h-12 w-full rounded-lg bg-gold text-sm font-bold text-bg disabled:opacity-50"
            >
              {busy ? "…" : mode === "save" ? "Save my village" : "Log in"}
            </button>
            {mode === "save" || username === null ? (
              <button
                type="button"
                onClick={() => switchTo(mode === "save" ? "login" : "save")}
                className="mt-2 h-10 w-full text-xs font-semibold text-ink-dim underline"
              >
                {mode === "save"
                  ? "I already saved a village — log in instead"
                  : "No saved village? Save this one instead"}
              </button>
            ) : null}
          </form>
        )}
      </div>
    </div>
  );
}
